import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Switch } from "@/components/ui/switch"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"

const formSchema = z.object({
  projectCode: z.string().optional(),
  notes: z.string().optional(),
  requiresCodeLiveTest: z.boolean().default(false),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  status: z.enum(["open", "pending", "closed"]),
})

export type AdditionalInfoFormData = z.infer<typeof formSchema>

interface AdditionalInfoFormProps {
  formData: AdditionalInfoFormData;
  updateFormData: (data: AdditionalInfoFormData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

export function AdditionalInfoForm({ formData, updateFormData, onNext, onPrevious }: AdditionalInfoFormProps) {
  const form = useForm<AdditionalInfoFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectCode: formData.projectCode || "",
      notes: formData.notes || "",
      requiresCodeLiveTest: formData.requiresCodeLiveTest || false,
      priority: formData.priority,
      status: formData.status,
    },
  })

  const onSubmit = (data: AdditionalInfoFormData) => {
    updateFormData(data)
    onNext()
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="projectCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Project Code</FormLabel>
              <FormControl>
                <Input placeholder="e.g., PRJ-2023-001" {...field} />
              </FormControl>
              <FormDescription>The internal project code associated with this request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Priority</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>The priority level of this resource request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>The current status of this resource request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="requiresCodeLiveTest"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Requires Code Live Test</FormLabel>
                <FormDescription>
                  Does this position require a live coding test during the interview process?
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any additional information or special requirements"
                  className="min-h-32"
                  {...field}
                />
              </FormControl>
              <FormDescription>Any additional information that might be relevant to this request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onPrevious}>
            Previous
          </Button>
          <Button type="submit">Next</Button>
        </div>
      </form>
    </Form>
  )
}

